import React from "react";
import { T } from "Components";

import DashboardCard from "./DashboardCard";
import useStatistics from "../../Infrastructure/useStatistics";
import { FormatNumber } from "../../Infrastructure/format";

/**
 * Birlik Tankları — birliğin kendi envanteri (Storage.TankInventory).
 *
 * GET /Statistics/UnionInventory → { Rows: [en çok 1 satır], TotalCount, TotalTonnage }.
 * Birlik sunucuda çalışılan şirketten alınır; admin Birlik Özeti'nde istek
 * StatisticsEndpointContext ile /admin/TobOverview/CompanyUnionInventory'ye
 * (seçili birlikle) yönlendirilir.
 *
 * Tank / Makine / Süt Ölçüm Cihazı adetleri ve yalnız tank kayıtlarının tonajı (S2).
 * Etiketler TOB Genel Bakış'taki Birlik Tankları kartının anahtarlarını
 * (dashboard.tob.uniontanks.*) kullanır — yeni resource gerekmez.
 * Renkler yalnız tema token'larından.
 */

const UNION_INVENTORY_URL = "/Statistics/UnionInventory";

const EMPTY_LIST = { Rows: [], TotalCount: 0, TotalTonnage: 0 };

/** Tonaj: tam sayıysa ondalıksız, değilse 1 ondalık. */
const tonnageText = (value) => {
  const amount = Number(value) || 0;
  return FormatNumber(amount, Number.isInteger(amount) ? 0 : 1);
};

const UnionInventory = () => {
  const { data, loading, error } = useStatistics(UNION_INVENTORY_URL, EMPTY_LIST);

  const source = data && typeof data === "object" ? data : EMPTY_LIST;
  const row = (Array.isArray(source.Rows) && source.Rows[0]) || {};
  const totalCount = Number(source.TotalCount) || 0;

  const stats = [
    { key: "tank", value: FormatNumber(Number(row.TankCount) || 0) },
    { key: "machine", value: FormatNumber(Number(row.MachineCount) || 0) },
    { key: "milkmeasurement", value: FormatNumber(Number(row.MilkMeasurementCount) || 0) },
    { key: "tanktonnage", value: tonnageText(row.TankTonnage), unit: T.Main("dashboard.tob.uniontanks.ton") },
  ];

  return (
    <DashboardCard
      className="fdash__unioninventory"
      title={T.Main("dashboard.tob.uniontanks.title")}
      description={`${FormatNumber(totalCount)} ${T.Main("dashboard.tob.uniontanks.records")}`}
      loading={loading}
      error={error}
      empty={totalCount === 0}
      emptyText={T.Main("dashboard.tob.uniontanks.empty")}
    >
      <div className="fdash__unioninventory__stats" style={styles.stats}>
        {stats.map((stat) => {
          const label = T.Main(`dashboard.tob.uniontanks.${stat.key}`);

          return (
            <div key={stat.key} style={styles.stat}>
              <span style={styles.label} title={label}>{label}</span>
              <span style={styles.value}>
                {stat.value}
                {stat.unit && <span style={styles.unit}>{` ${stat.unit}`}</span>}
              </span>
            </div>
          );
        })}
      </div>
    </DashboardCard>
  );
};

// Satır içi düzen — modül bundle'ı CSS taşımıyor ve AdminUI scss'e dokunulmuyor.
// Yalnız token kullanılır; sabit renk yok.
const styles = {
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
    gap: 14,
    fontVariantNumeric: "tabular-nums",
  },
  stat: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
    padding: "10px 12px",
    borderRadius: 8,
    background: "var(--surface-2)",
  },
  label: {
    fontSize: 11.5,
    fontWeight: 600,
    color: "var(--text-3)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  value: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: "-.02em",
    color: "var(--text)",
  },
  unit: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--text-2)",
  },
};

export default UnionInventory;
